import { useLocation } from "react-router-dom";
import HelpLink from "@/components/HelpLink";
import { pageHelpTopic, fieldHelpTopics } from "@/lib/userGuide.mjs";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const BADGE = {
  amber: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
  sky: "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30",
  emerald: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  indigo: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border-indigo-500/30",
  slate: "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30",
  purple: "bg-purple-500/15 text-purple-600 dark:text-purple-400 border-purple-500/30",
  blue: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30 animate-pulse",
  rose: "bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30",
};
const STATUS = {
  brouillon: ["amber", "Brouillon"], test: ["sky", "Test"], "publiée": ["emerald", "Publiée"], publie: ["emerald", "Publié"],
  "archivée": ["slate", "Archivée"], archived: ["slate", "Archivé"], active: ["emerald", "Actif"], inactive: ["slate", "Inactif"],
  "configuré": ["indigo", "Configuré"], "non configuré": ["slate", "Non configuré"], "simulé": ["purple", "Simulé"],
  "repli navigateur": ["amber", "Repli navigateur"], "repli local": ["amber", "Repli local"], "non implémenté": ["slate", "Non implémenté"],
  en_attente: ["slate", "En attente"], extraction: ["blue", "Extraction"], indexation: ["blue", "Indexation"], disponible: ["emerald", "Disponible"],
  "réutilisé": ["sky", "Réutilisé"], erreur: ["rose", "Erreur"], "succès": ["emerald", "Succès"], "bloqué": ["rose", "Bloqué"], "refusé": ["rose", "Refusé"],
  "confirmation requise": ["amber", "Confirmation requise"], "annulé par l'utilisateur": ["slate", "Annulé"], "en cours": ["blue", "En cours"],
  "terminé": ["slate", "Terminé"], "transféré": ["sky", "Transféré"], read: ["sky", "Consultation"], write: ["amber", "Modification"],
  demo: ["purple", "Démo"], accueil: ["indigo", "Accueil"], specialise: ["sky", "Spécialisé"], secours: ["amber", "Secours"],
  "à revoir": ["amber", "À revoir"], "traité": ["emerald", "Traité"], ok: ["emerald", "OK"],
};

export function StatusBadge({ status, label, testid }) {
  const [c, l] = STATUS[status] || ["slate", status];
  return <Badge variant="outline" data-testid={testid} className={`${BADGE[c]} font-medium text-[11px] whitespace-normal sm:whitespace-nowrap`}>{label || l}</Badge>;
}

export const DemoBadge = ({ show }) => (show ? <StatusBadge status="demo" label="Donnée de démo" /> : null);

export function PageHeader({ title, subtitle, children, helpTopic }) {
  const location = useLocation();
  const topic = helpTopic || pageHelpTopic(location.pathname, location.search);
  return (
    <div className="page-heading fade-in">
      <div>
        <h1 className="tracking-tight" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{subtitle}</p>}
      </div>
      <div className="flex gap-2 flex-wrap items-center">{topic && <HelpLink topic={topic} label={typeof title === "string" ? title : undefined} compact={false} />}{children}</div>
    </div>
  );
}

export function Field({ label, hint, children, className = "", helpTopic }) {
  const topic = helpTopic || (typeof label === "string" ? fieldHelpTopics[label] : null);
  return (
    <div className={`space-y-1.5 ${className}`}>
      <div className="field-label-row"><Label className="field-label">{label}</Label>{topic && <HelpLink topic={topic} label={label} />}</div>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function Sel({ value, onChange, options, placeholder = "Choisir…", testid, className = "" }) {
  return (
    <Select value={value ?? ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={className}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function LinesInput({ value, onChange, testid, rows = 3, placeholder }) {
  return (
    <Textarea data-testid={testid} rows={rows} placeholder={placeholder || "Une entrée par ligne"} value={(value || []).join("\n")}
      onChange={(e) => onChange(e.target.value.split("\n").filter((x, i, a) => x.trim() || i === a.length - 1))} />
  );
}

export function JsonBlock({ data, className = "" }) {
  return <pre className={`mono text-[11px] bg-muted/50 border rounded-md p-3 overflow-auto max-h-72 whitespace-pre-wrap break-all ${className}`}>{typeof data === "string" ? data : JSON.stringify(data, null, 2)}</pre>;
}

export function Empty({ children }) {
  return <div className="text-sm text-muted-foreground border border-dashed rounded-xl p-8 text-center">{children}</div>;
}

export function Can({ perm, children }) {
  const { can } = useAuth();
  return can(perm) ? children : null;
}

export function Card({ children, className = "", ...p }) {
  return <div className={`surface min-w-0 p-5 ${className}`} {...p}>{children}</div>;
}

export function Kpi({ label, value, sub, testid }) {
  return (
    <Card className="fade-in">
      <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
      <div className="text-3xl font-bold mt-2" style={{ fontFamily: "Outfit" }} data-testid={testid}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
}
